import type { Table } from '@tanstack/react-table';
import * as React from 'react';
import { getPageNumbers, PAGINATION_ELLIPSES } from './data-grid-pagination.util';
import { ChevronLeft, ChevronRight, CaptionTypography } from '@components/atoms';
import { Button, ButtonSize, ButtonVariant } from '@components/molecules';
import { cn } from '@utils';

interface DataTablePaginationProps<TData> {
	table: Table<TData>;
	className?: string;
	children?: React.ReactNode;
}

const MAX_COMPACT_PAGE_DIGITS = 3;

export const DataTablePagination = <TData,>({
	table,
	className,
	children,
}: DataTablePaginationProps<TData>) => {
	const pageCount = table.getPageCount();
	const currentPage = table.getState().pagination.pageIndex;

	const pageNumbers = getPageNumbers(pageCount, currentPage);

	return (
		<div data-aid="data-grid-pagination-container" className={cn('flex items-center justify-between', className)}>
			<div>{children}</div>
			<div className="flex items-center space-x-1">
				<Button
					variant={ButtonVariant.ICON}
					size={ButtonSize.XS}
					onClick={() => table.previousPage()}
					disabled={!table.getCanPreviousPage()}
				>
					<span className="sr-only">Go to previous page</span>
					<ChevronLeft className="h-4 w-4" />
				</Button>
				{pageNumbers.map((page, index) => {
					const selectedPage = currentPage === page;

					if (page === PAGINATION_ELLIPSES) {
						return (
							<CaptionTypography key={index} className="align-center items-center justify-center">
								{PAGINATION_ELLIPSES}
							</CaptionTypography>
						);
					}

					const pageLabel = Number(page) + 1;
					const isLongPageNumber = String(pageLabel).length > MAX_COMPACT_PAGE_DIGITS;

					return (
						<Button
							key={index}
							size={ButtonSize.XS}
							className={cn(
								isLongPageNumber ? 'aspect-auto h-6 w-auto min-w-6 px-2' : 'h-6 w-6',
								selectedPage ? 'cursor-pointer! bg-accent!' : 'bg-none hover:bg-gray-100'
							)}
							variant={selectedPage ? ButtonVariant.PRIMARY : ButtonVariant.ICON}
							disabled={selectedPage}
							onClick={() => table.setPageIndex(Number(page))}
						>
							<CaptionTypography className={`${!selectedPage ? 'text-primary' : 'text-white'}`}>
								{pageLabel}
							</CaptionTypography>
						</Button>
					);
				})}
				<Button
					variant={ButtonVariant.ICON}
					size={ButtonSize.XS}
					onClick={() => table.nextPage()}
					disabled={!table.getCanNextPage()}
				>
					<span className="sr-only">Go to next page</span>
					<ChevronRight className="h-4 w-4" />
				</Button>
			</div>
		</div>
	);
};
